import { Link, useLocation, useParams } from "react-router-dom";
import { BiRightArrow } from "react-icons/bi";
import { useAppSelector } from "@app/hooks";
import { selectCurrentProject } from "@app/features/project/projectSlice";
import { useContextAwareGetModulesStatuses, useGetStatusTypesQuery } from "@hooks/index";
import { useMemo } from "react";
import { encodeProjectId } from "@utils/projectIdEncoder";
import Button from "@components/button/Button";
import Skeleton from "react-loading-skeleton";
import { TopbarStep } from "@typedefs/interfaces";
import { FEModules, activityCompleteInfo } from "@utils/moduleList";
import StepLink from "./StepLink";
import StepSkeleton from "./StepSkeleton";

interface TopbarStepsProps {
	projectLabel?: string;
	activityLabel?: string;
	stepsList: TopbarStep[];
	isLoading?: boolean;
	isPublic?: boolean;
}

const TopbarSteps = ({
	projectLabel,
	activityLabel,
	stepsList,
	isLoading,
	isPublic = false,
}: TopbarStepsProps) => {
	const { pathname } = useLocation();
	const pathSegments = pathname.split("/").filter(Boolean);
	const isInBuilder = isPublic ? pathSegments[1] === "builder" : pathSegments[0] === "builder";
	const finalPath = pathSegments[pathSegments.length - 1];
	const { projectId: routeProjectId, activityId: routeActivityId } = useParams<{
		projectId?: string;
		activityId?: string;
	}>();

	const stepBase = useMemo(() => {
		if (isInBuilder) {
			if (!routeActivityId) return undefined;
			if (isPublic) {
				return pathSegments[0] === "public" && pathSegments[1] === "builder"
					? `/public/builder/${routeActivityId}`
					: undefined;
			}
			return pathSegments[0] === "builder" ? `/builder/${routeActivityId}` : undefined;
		}
		if (!routeProjectId) return undefined;
		if (isPublic) {
			return pathSegments[0] === "public" && pathSegments[1] === "project"
				? `/public/project/${routeProjectId}`
				: undefined;
		}
		return pathSegments[0] === "project" ? `/project/${routeProjectId}` : undefined;
	}, [isInBuilder, isPublic, pathname, routeProjectId, routeActivityId]);

	const { activeActivityId, project } = useAppSelector(selectCurrentProject);
	const { data: moduleStatuses } = useContextAwareGetModulesStatuses(activeActivityId ?? 0, { skip: !activeActivityId });
	const { data: statusTypes } = useGetStatusTypesQuery();

	const areAllModulesReady = useMemo(() =>
		moduleStatuses?.filter((status) => status.feModuleId !== FEModules.ActivityComplete).every((status) => statusTypes?.find((type) => type.id === status.status)?.name_en === "READY"),
		[moduleStatuses]
	);
	const areSomeModulesReady = useMemo(() =>
		moduleStatuses?.filter((status) => status.feModuleId !== FEModules.ActivityComplete).some((status) => statusTypes?.find((type) => type.id === status.status)?.name_en === "READY"),
		[moduleStatuses]
	);
	
	const url = useMemo(() => {
		if (isPublic) {
			if (!project?.id) {
				return `/public/project/new/activities`;
			}
			try {
				const encodedId = encodeProjectId(project.id);
				return `/public/project/${encodedId}/activities`;
			} catch (error) {
				console.error('Failed to encode project ID in topbar:', error);
				return `/public/project/${project.id}/activities`;
			}
		}
		return `/project/${project?.id ?? "new"}/activities`;
	}, [isPublic, project?.id]);

	const renderSteps = () => {
		if (isLoading) {
			return <StepSkeleton count={3} />;
		}

		const stepsToRender = isInBuilder ? [...stepsList, activityCompleteInfo] : stepsList;

		return stepsToRender.map((step, index) => {
			const hasNext = isInBuilder ? index < stepsList.length : index < stepsList.length - 1;
			const isActive = finalPath === step.path;
			const statusName = step.id === FEModules.ActivityComplete
				? (areAllModulesReady ? "READY" : areSomeModulesReady ? "IN PROGRESS" : "EMPTY")
				: statusTypes?.find((statusType) => moduleStatuses?.find((status) => status.feModuleId === step.id)?.status === statusType.id)?.name_en;

			const absoluteTo = stepBase ? `${stepBase}/${step.path}` : undefined;

			return (
				<StepLink
					key={step.id}
					step={step}
					index={index}
					isActive={isActive}
					hasNext={hasNext}
					statusName={statusName}
					absoluteTo={absoluteTo}
				/>
			);
		});
	};

	return (
		<div className="steps-container fs-13">
			<div className="steps-labels">
				{activityLabel && (
					<Link to={url} className="no-decoration">
						{isLoading ?
							<Skeleton width={150} height={20} />
							:
							<Button whileHoverProps={{ backgroundColor: "var(--gray)" }} className="me-1 p-1 ff-normal-ext fs-12 lh-90 br-8">
								{activityLabel}
							</Button>
						}
					</Link>
				)}
				{projectLabel && (
					<>
						{isLoading ?
							<Skeleton width={120} height={20} className="ms-2" /> :
							<div className="d-flex">
								<span className="fs-13 ff-bold-ext project-label">{projectLabel}</span>
							</div>
						}
						{stepsList.length > 0 && <div className="mx-1"><BiRightArrow size={24} /></div>}
					</>
				)}
			</div>
			{renderSteps()}
		</div>
	);
};

export default TopbarSteps;
